// apps/app/src/components/layout/command-palette.tsx
//
// ⌘K. Jumps to a patient or a common action. Built on cmdk, so arrow keys,
// Enter and Escape behave the way every other palette does — and navigation is
// client-side, where the previous version reloaded the whole page on Enter.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, PanelsTopLeft, Sparkles, UserPlus, UserRound } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { http } from '@/lib/api-client';

interface PatientHit {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

// `labelKey` is in the common namespace, translated at render.
const ACTIONS = [
  { labelKey: 'palette.action.addTreatment', href: '/treatments?new=1', icon: Sparkles },
  { labelKey: 'palette.action.installSnippet', href: '/widget/install', icon: PanelsTopLeft },
  { labelKey: 'palette.action.billing', href: '/settings/billing', icon: CreditCard },
  { labelKey: 'palette.action.invite', href: '/settings/team', icon: UserPlus },
];

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [patients, setPatients] = useState<PatientHit[]>([]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setPatients([]);
      return;
    }
    const timer = setTimeout(() => {
      void http
        .get<{ data: PatientHit[] }>(`/patients?search=${encodeURIComponent(query.trim())}&limit=6`)
        .then((r) => setPatients(r.data))
        .catch(() => setPatients([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  const go = (href: string) => {
    onOpenChange(false);
    setQuery('');
    navigate(href);
  };

  return (
    // Filtering is done by the API for patients; actions are filtered by cmdk.
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('palette.placeholder')} value={query} onValueChange={setQuery} />
      <CommandList>
        <CommandEmpty>{t('palette.empty')}</CommandEmpty>

        {patients.length > 0 && (
          <CommandGroup heading={t('palette.patients')}>
            {patients.map((p) => {
              const name = [p.firstName, p.lastName].filter(Boolean).join(' ') || t('palette.unnamed');
              // The API already matched these — on fields cmdk cannot see — so the
              // query is part of the value and cmdk never filters them back out.
              return (
                <CommandItem key={p.id} value={`${query} ${name} ${p.id}`} onSelect={() => go(`/patients/${p.id}`)}>
                  <UserRound aria-hidden="true" />
                  <span className="flex-1">{name}</span>
                  {p.email && <span className="text-xs text-muted-foreground">{p.email}</span>}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        <CommandGroup heading={t('palette.actions')}>
          {ACTIONS.map((a) => {
            // cmdk filters on the value, so it is the label the person reads.
            const label = t(a.labelKey);
            return (
              <CommandItem key={a.href} value={label} onSelect={() => go(a.href)}>
                <a.icon aria-hidden="true" />
                {label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
